import type { ComponentProps } from 'react';
import React, { useMemo } from 'react';
import styled from '@emotion/styled';
import {
  borderColor,
  borderStyle,
  borderWidth,
  display,
  height,
  marginRight,
  space,
} from 'styled-system';
import type {
  BorderColorProps,
  BorderStyleProps,
  BorderWidthProps,
  DisplayProps,
  HeightProps,
  MarginRightProps,
} from 'styled-system';

import { NextControl, PrevControl } from 'common/components/Carousel/controls/CarouselControls';
import { useCarouselNavigation } from 'common/components/Carousel/hooks/use-carousel-navigation';
import { Flex } from 'common/components/Flex';
import { EventBusEvent } from 'common/enums/EventBus';
import eventBus from 'common/event-bus/EventBus';
import { useLocalization } from 'common/providers/LocaleProvider';
import type { SpaceProps } from 'common/theme/system-types';
import { borderLeftWidth } from 'common/theme/system-utilities';
import type { BorderLeftWidthProps } from 'common/theme/system-utilities';
import { Element } from 'common/types/tracking';
import { createGroupDisplayFilter, FilterGroupType, groupFilters } from 'common/utils/filterGroups';
import CategorySortOptions from '../../category-sort-options/components/CategorySortOptions';
import { List, listStyles } from '../../common-tab-bar/components/CommonTabBar';
import CustomFilterChip from './CustomFilterChip';
import { EventType } from './enums';
import { usePromotedFiltersActions } from './hooks';
import { usePromotedFiltersSlideAnimation } from './hooks/usePromotedFiltersSlideAnimation';
import { PromotedFilterButton } from './PromotedFilterButton';
import PromotedFilterChip from './PromotedFilterChip';
import type {
  CustomFilterChipsProps,
  FilterDividerProps,
  FilterGroupsProps,
  FilterProps,
  PromotedFiltersProps,
} from './types';

const PROMOTED_FILTERS_COUNT = 5;

const promotedFiltersWrapperStyles: ComponentProps<typeof Flex> = {
  alignItems: 'center',
  justifyContent: 'flex-start',
  gap: 'sp_12',
  overflow: 'hidden',
  width: '100%',
  position: 'relative',
  boxSizing: 'border-box',
};

const Divider = styled.div<
  BorderWidthProps &
    BorderLeftWidthProps &
    BorderStyleProps &
    BorderColorProps &
    HeightProps &
    SpaceProps
>`
  ${borderWidth};
  ${borderLeftWidth};
  ${borderStyle};
  ${borderColor};
  ${height};
  ${space};
`;

const dividerStyles: ComponentProps<typeof Divider> = {
  borderWidth: 0,
  borderLeftWidth: 1,
  borderStyle: 'solid',
  borderColor: 'sys.neutral.border.weak',
  height: '24px',
  mx: 'sp_4',
};

const CategorySortOptionsContainer = styled.li<DisplayProps & MarginRightProps>`
  ${display};
  ${marginRight};
`;

const categorySortOptionsContainerStyles: ComponentProps<typeof CategorySortOptionsContainer> = {
  display: ['block', null, 'none'],
  mr: 'sp_8',
};

const CustomFilterChips = ({ customFilters, onChipClick, onObserve }: CustomFilterChipsProps) =>
  customFilters.map((customFilter, index) => (
    <CustomFilterChip
      key={customFilter.key}
      onClick={() =>
        onChipClick({
          initiallyExpanded: [customFilter.key],
          label: customFilter.name,
          listPosition: index,
          element: Element.Link,
          eventType: EventType.FilterAttribute,
        })
      }
      onPromotedFilterViewed={() => onObserve(customFilter, EventType.FilterAttribute)}
      customFilter={{
        ...customFilter.filters[0],
        name: customFilter.name,
      }}
      filterKey={customFilter.key}
    />
  ));

const FilterDivider = ({ customFiltersLength, processedFiltersLength }: FilterDividerProps) => {
  if (customFiltersLength === 0 || processedFiltersLength === 0) {
    return null;
  }

  return (
    <li role="separator" aria-hidden="true">
      <Divider data-testid="divider" {...dividerStyles} />
    </li>
  );
};

const GroupedFilter = ({ filterGroup, listPosition, onChipClick, onObserve }: FilterProps) => {
  const combinedFilter = createGroupDisplayFilter(filterGroup);

  return (
    <PromotedFilterChip
      filter={combinedFilter}
      onClick={() =>
        onChipClick({
          initiallyExpanded: [filterGroup.key],
          label: filterGroup.name,
          listPosition,
          element: Element.Button,
          eventType: EventType.FilterSection,
        })
      }
      onPromotedFilterViewed={() => onObserve(filterGroup, EventType.FilterSection)}
      showIcon
    />
  );
};

const IndividualFilter = ({ filterGroup, listPosition, onChipClick, onObserve }: FilterProps) => {
  const filter = filterGroup.filters[0];
  if (!filter) {
    return null;
  }

  return (
    <PromotedFilterChip
      filter={filter}
      onClick={() =>
        onChipClick({
          initiallyExpanded: [filter.key],
          label: filter.name,
          listPosition,
          element: Element.Button,
          eventType: EventType.FilterSection,
        })
      }
      onPromotedFilterViewed={() => onObserve(filterGroup, EventType.FilterSection)}
      showIcon
    />
  );
};

const FilterGroups = ({
  filterGroups,
  customFiltersLength,
  onChipClick,
  onObserve,
}: FilterGroupsProps) => (
  <>
    {filterGroups.map((filterGroup, index) => {
      const listPosition = customFiltersLength + index;

      return filterGroup.type === FilterGroupType.Grouped ? (
        <GroupedFilter
          key={filterGroup.key}
          filterGroup={filterGroup}
          listPosition={listPosition}
          onChipClick={onChipClick}
          onObserve={onObserve}
        />
      ) : (
        <IndividualFilter
          key={filterGroup.key}
          filterGroup={filterGroup}
          listPosition={listPosition}
          onChipClick={onChipClick}
          onObserve={onObserve}
        />
      );
    })}
  </>
);

const PromotedFilters = ({
  filters: allFilters = [],
  customFilters = [],
  selectedFiltersCount = 0,
  categorySortOptions,
}: PromotedFiltersProps) => {
  const { t } = useLocalization();

  const filterGroups = useMemo(() => {
    return groupFilters(allFilters).slice(0, PROMOTED_FILTERS_COUNT);
  }, [allFilters]);

  const openDrawer = (initiallyExpanded: string[]) => {
    eventBus.emit(EventBusEvent.ToggleCategoryFiltersDrawer, {
      open: true,
      headline: t('h24_filter_sort'),
      excludeCategory: false,
      isSort: false,
      initiallyExpanded,
    });
  };

  const { handleObserve, handleChipClick, handleFilterButtonClick } = usePromotedFiltersActions({
    filters: filterGroups,
    customFilters,
    onOpenDrawer: openDrawer,
  });

  const { listRef, hasPrevControl, hasNextControl, updateControls, onPreviousSlide, onNextSlide } =
    useCarouselNavigation();

  usePromotedFiltersSlideAnimation(listRef);

  if (!allFilters.length && !customFilters.length) {
    return null;
  }

  return (
    <Flex
      data-corgi-id="promoted-filters"
      role="region"
      aria-label={t('h24_filters')}
      backgroundColor="sys.neutral.background.hover"
      px={['sp_0', 'sp_0', 'sp_48']}
      {...promotedFiltersWrapperStyles}
    >
      {hasPrevControl && <PrevControl handleClick={onPreviousSlide} isPromotedFilters />}
      <List
        {...listStyles}
        ref={listRef}
        onScroll={updateControls}
        alignItems="center"
        paddingTop="sp_4"
        paddingBottom="sp_2"
        px={['sp_16', 'sp_20', 'sp_2']}
        gap="sp_0"
        aria-label={t('h24_filters')}
        aria-roledescription={t('h24_carousel')}
      >
        <CategorySortOptionsContainer {...categorySortOptionsContainerStyles}>
          <CategorySortOptions
            order={categorySortOptions.order}
            sortOptions={categorySortOptions.sortOptions}
            withShopInShopNav={categorySortOptions.withShopInShopNav}
          />
        </CategorySortOptionsContainer>
        <PromotedFilterButton
          onClick={handleFilterButtonClick}
          selectedFiltersCount={selectedFiltersCount}
        />
        <CustomFilterChips
          customFilters={customFilters}
          onChipClick={handleChipClick}
          onObserve={handleObserve}
        />
        <FilterDivider
          customFiltersLength={customFilters.length}
          processedFiltersLength={filterGroups.length}
        />
        <FilterGroups
          filterGroups={filterGroups}
          onChipClick={handleChipClick}
          onObserve={handleObserve}
          customFiltersLength={customFilters.length}
        />
      </List>
      {hasNextControl && <NextControl handleClick={onNextSlide} isPromotedFilters />}
    </Flex>
  );
};

export default PromotedFilters;
